import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { useCurrentStore } from '../context/CurrentStoreContext'
import { Card, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Select } from '../components/ui/Select'
import { ImageCropDialog } from '../components/ui/ImageCropDialog'
import { FaviconSettings } from '../components/FaviconSettings'
import { Modal } from '../components/ui/Modal'
import { UploadProgressBar } from '../components/ui/UploadProgressBar'
import { StatusModal, useStatusModal } from '../components/ui/StatusModal'
import * as storeApi from '../services/storeService'
import { uploadProductImages } from '../services/uploadService'
import { draftProfileText } from '../services/storefrontAiService'
import { getUserFacingErrorMessage } from '../utils/userFacingError'
import { coerceStoreSocialLinks } from '../utils/storeSocialLinks'
import { CURRENCIES, getCurrencyLabel } from '../utils/currencies'
import { STORE_LANGUAGES, normalizeLanguageChoice, type StoreLanguageCode } from '../utils/storeLanguages'
import { DEFAULT_LOW_STOCK_THRESHOLD, normalizeLowStockThreshold } from '../lib/inventoryStock'
import { STANDARD_ADDRESS_PLACEHOLDER } from '@/lib/formatStandardAddress'
import {
  fetchSubscription,
  updateOrganisationCountry,
  type MerchantSubscription,
} from '../services/billingApi'
import { listCountries, type GeoCountryItem } from '../services/geoReferenceService'

function orgDefaultCurrency(store: storeApi.StoreRecord | undefined): string {
  return String(store?.organisation?.currency ?? 'USD').trim().toUpperCase() || 'USD'
}

function storefrontCurrencyChoice(store: storeApi.StoreRecord | undefined): string {
  if (!store) return ''
  const orgCur = orgDefaultCurrency(store)
  const effective = String(store.currency ?? '').trim().toUpperCase()
  if (!effective || effective === orgCur) return ''
  return effective
}

const textareaClass =
  'w-full rounded-2xl border border-input bg-background px-4 py-2.5 text-sm text-foreground shadow-sm transition-all duration-150 placeholder:text-muted-foreground hover:border-border focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-60 min-h-[88px] resize-y'

function syncProfileFromRow(row: storeApi.StoreRecord | undefined) {
  if (!row) {
    return {
      aboutText: '',
      tagline: '',
      sellingSummary: '',
      contactAddress: '',
      businessHours: '',
      contactEmail: '',
      contactPhone: '',
      whatsapp: '',
      logo: '',
      faviconUrl: '',
      aboutBannerUrl: '',
    }
  }
  return {
    aboutText: row.aboutText ?? '',
    tagline: row.tagline ?? '',
    sellingSummary: row.sellingSummary ?? '',
    contactAddress: row.contactAddress ?? '',
    businessHours: row.businessHours ?? '',
    contactEmail: row.contactEmail ?? '',
    contactPhone: row.contactPhone ?? '',
    whatsapp: coerceStoreSocialLinks(row.socialLinks).whatsapp ?? '',
    logo: row.logo ?? '',
    faviconUrl: row.faviconUrl ?? '',
    aboutBannerUrl: row.aboutBannerUrl ?? '',
  }
}

export default function Settings() {
  const { user } = useAuth()
  const { currentStoreId: storeId, storeRecord, loading, refreshStores, applyStoreRecord } = useCurrentStore()
  const logoInputRef = useRef<HTMLInputElement | null>(null)
  const bannerInputRef = useRef<HTMLInputElement | null>(null)
  const [taxRatePercent, setTaxRatePercent] = useState('0')
  const [lowStockThreshold, setLowStockThreshold] = useState(String(DEFAULT_LOW_STOCK_THRESHOLD))
  const [inventorySaving, setInventorySaving] = useState(false)
  const [currencySaving, setCurrencySaving] = useState(false)
  const [currencyChoice, setCurrencyChoice] = useState('')
  const [languagesSaving, setLanguagesSaving] = useState(false)
  const [defaultLocaleChoice, setDefaultLocaleChoice] = useState<StoreLanguageCode>('en')
  const [supportedLocalesChoice, setSupportedLocalesChoice] = useState<StoreLanguageCode[]>(['en'])
  const [countries, setCountries] = useState<GeoCountryItem[]>([])
  const [countryIso2, setCountryIso2] = useState('')
  const [countrySaving, setCountrySaving] = useState(false)
  const [billingPreview, setBillingPreview] = useState<MerchantSubscription | null>(null)
  const [saving, setSaving] = useState(false)
  const [profileSaving, setProfileSaving] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [uploadingBanner, setUploadingBanner] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [logoCrop, setLogoCrop] = useState<{ url: string; mime: string } | null>(null)
  const [bannerCrop, setBannerCrop] = useState<{ url: string; mime: string } | null>(null)
  const [previewImage, setPreviewImage] = useState<{ url: string; title: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { openSuccess, openError, openInfo, statusModalProps } = useStatusModal()
  const [aiTaglineLoading, setAiTaglineLoading] = useState(false)
  const [aiAboutLoading, setAiAboutLoading] = useState(false)
  const [checklistResetting, setChecklistResetting] = useState(false)

  const [aboutText, setAboutText] = useState('')
  const [tagline, setTagline] = useState('')
  const [sellingSummary, setSellingSummary] = useState('')
  const [contactAddress, setContactAddress] = useState('')
  const [businessHours, setBusinessHours] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [faviconUrl, setFaviconUrl] = useState('')
  const [aboutBannerUrl, setAboutBannerUrl] = useState('')

  useEffect(() => {
    void listCountries()
      .then(setCountries)
      .catch(() => setCountries([]))
  }, [])

  useEffect(() => {
    if (user?.role !== 'org_admin') return
    void fetchSubscription()
      .then((sub) => {
        setBillingPreview(sub)
        if (sub?.contactCountryCode) {
          setCountryIso2(sub.contactCountryCode.toUpperCase())
        } else if (sub?.contactCountry && countries.length) {
          const match = countries.find(
            (c) => c.name.toLowerCase() === sub.contactCountry!.trim().toLowerCase(),
          )
          if (match) setCountryIso2(match.iso2)
        }
      })
      .catch(() => setBillingPreview(null))
  }, [user?.role, countries])

  useEffect(() => {
    if (!storeRecord) {
      setTaxRatePercent('0')
      setLowStockThreshold(String(DEFAULT_LOW_STOCK_THRESHOLD))
      setCurrencyChoice('')
      setDefaultLocaleChoice('en')
      setSupportedLocalesChoice(['en'])
      return
    }
    setTaxRatePercent(String(storeRecord.taxRatePercent ?? 0))
    setLowStockThreshold(String(normalizeLowStockThreshold(storeRecord.lowStockThreshold)))
    setCurrencyChoice(storefrontCurrencyChoice(storeRecord))
    {
      const langs = normalizeLanguageChoice(storeRecord.defaultLocale, storeRecord.supportedLocales)
      setDefaultLocaleChoice(langs.defaultLocale)
      setSupportedLocalesChoice(langs.supportedLocales)
    }
    const p = syncProfileFromRow(storeRecord)
    setAboutText(p.aboutText)
    setTagline(p.tagline)
    setSellingSummary(p.sellingSummary)
    setContactAddress(p.contactAddress)
    setBusinessHours(p.businessHours)
    setContactEmail(p.contactEmail)
    setContactPhone(p.contactPhone)
    setWhatsapp(p.whatsapp)
    setLogoUrl(p.logo)
    setFaviconUrl(p.faviconUrl)
    setAboutBannerUrl(p.aboutBannerUrl)
  }, [storeRecord])

  async function handleSaveTax() {
    if (storeId == null) return
    setSaving(true)
    setError(null)
    const n = Number(taxRatePercent)
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      setError('Tax rate must be between 0 and 100.')
      setSaving(false)
      return
    }
    try {
      await storeApi.updateStore(storeId, { taxRatePercent: n })
      await refreshStores()
      openSuccess({
        title: 'Tax rate saved',
        description: 'Checkout will use this rate on the order subtotal after line discounts.',
      })
    } catch (e) {
      openError({
        title: 'Could not save tax rate',
        description: getUserFacingErrorMessage(e, 'Failed to save tax rate'),
      })
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveLowStockThreshold() {
    if (storeId == null) return
    setInventorySaving(true)
    setError(null)
    const n = normalizeLowStockThreshold(lowStockThreshold)
    try {
      await storeApi.updateStore(storeId, { lowStockThreshold: n })
      setLowStockThreshold(String(n))
      await refreshStores()
      openSuccess({
        title: 'Low-stock threshold saved',
        description: `Products with ${n} units or fewer will appear in low-stock alerts and filters.`,
      })
    } catch (e) {
      openError({
        title: 'Could not save inventory setting',
        description: getUserFacingErrorMessage(e, 'Failed to save low-stock threshold'),
      })
    } finally {
      setInventorySaving(false)
    }
  }

  const savedDefaultLocale = normalizeLanguageChoice(storeRecord?.defaultLocale, storeRecord?.supportedLocales).defaultLocale
  const defaultLocaleIsChanging = defaultLocaleChoice !== savedDefaultLocale
  const languageLabel = (code: string) => STORE_LANGUAGES.find((l) => l.code === code)?.label.split(' (')[0] ?? code

  function toggleSupportedLocale(code: StoreLanguageCode, on: boolean) {
    setSupportedLocalesChoice((prev) =>
      normalizeLanguageChoice(defaultLocaleChoice, on ? [...prev, code] : prev.filter((c) => c !== code))
        .supportedLocales,
    )
  }

  function handleDefaultLocaleChange(code: StoreLanguageCode) {
    setDefaultLocaleChoice(code)
    // The default language is always offered.
    setSupportedLocalesChoice((prev) => normalizeLanguageChoice(code, prev).supportedLocales)
  }

  async function handleSaveLanguages() {
    if (storeId == null) return
    const next = normalizeLanguageChoice(defaultLocaleChoice, supportedLocalesChoice)
    if (
      defaultLocaleIsChanging &&
      !window.confirm(
        `Your products and store text will now be treated as written in ${languageLabel(next.defaultLocale)}. ` +
          `Automatic translations will be redone, and translations you edited will be flagged for review. ` +
          `Only continue if your text really is in ${languageLabel(next.defaultLocale)}.`,
      )
    ) {
      return
    }
    setLanguagesSaving(true)
    setError(null)
    try {
      const updated = await storeApi.updateStore(storeId, next)
      applyStoreRecord(updated)
      await refreshStores()
      openSuccess({
        title: 'Storefront languages saved',
        description:
          next.supportedLocales.length > 1
            ? `Customers can switch between ${next.supportedLocales.length} languages. New customers start in ${
                STORE_LANGUAGES.find((l) => l.code === next.defaultLocale)?.label
              }.`
            : 'Your shop is shown in one language, so the language switcher is hidden.',
      })
    } catch (e) {
      openError({
        title: 'Could not save languages',
        description: getUserFacingErrorMessage(e, 'Failed to save storefront languages'),
      })
    } finally {
      setLanguagesSaving(false)
    }
  }

  async function handleSaveCurrency() {
    if (storeId == null) return
    setCurrencySaving(true)
    setError(null)
    const orgCur = orgDefaultCurrency(storeRecord)
    const next =
      currencyChoice === '' || currencyChoice === orgCur ? null : currencyChoice.toUpperCase()
    try {
      await storeApi.updateStore(storeId, { currency: next })
      await refreshStores()
      const label = getCurrencyLabel(next ?? orgCur)
      openSuccess({
        title: 'Storefront currency saved',
        description: next
          ? `Customers will see prices in ${label}. This shop no longer follows the company default.`
          : `Customers will see prices in ${label} (company default).`,
      })
    } catch (e) {
      openError({
        title: 'Could not save currency',
        description: getUserFacingErrorMessage(e, 'Failed to save storefront currency'),
      })
    } finally {
      setCurrencySaving(false)
    }
  }

  async function handleSaveCountry() {
    if (user?.role !== 'org_admin') return
    const selected = countries.find((c) => c.iso2 === countryIso2)
    if (!selected) {
      openError({ title: 'Select a country', description: 'Choose your organisation country first.' })
      return
    }
    setCountrySaving(true)
    try {
      const result = await updateOrganisationCountry({
        contactCountry: selected.name,
        contactCountryCode: selected.iso2,
      })
      setBillingPreview((prev) =>
        prev
          ? {
              ...prev,
              contactCountry: result.contactCountry,
              contactCountryCode: result.contactCountryCode,
              billingLocked: result.billingLocked,
              pendingBillingCurrency: result.pendingBillingCurrency,
              pendingBillingProvider: result.pendingBillingProvider,
            }
          : prev,
      )
      openSuccess({
        title: 'Organisation country saved',
        description: result.billingLocked
          ? `Country updated. Billing stays locked to ${result.pendingBillingCurrency} (${result.pendingBillingProvider === 'paystack' ? 'Paystack' : 'Mollie'}).`
          : `You will be charged in ${result.pendingBillingCurrency} via ${result.pendingBillingProvider === 'paystack' ? 'Paystack' : 'Mollie'} until first payment.`,
      })
    } catch (e) {
      openError({
        title: 'Could not save country',
        description: getUserFacingErrorMessage(e, 'Failed to save organisation country'),
      })
    } finally {
      setCountrySaving(false)
    }
  }

  function closeLogoCrop() {
    if (logoCrop?.url) URL.revokeObjectURL(logoCrop.url)
    setLogoCrop(null)
    if (logoInputRef.current) logoInputRef.current.value = ''
  }

  function closeBannerCrop() {
    if (bannerCrop?.url) URL.revokeObjectURL(bannerCrop.url)
    setBannerCrop(null)
    if (bannerInputRef.current) bannerInputRef.current.value = ''
  }

  function handleLogoFile(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0]
    if (!file || storeId == null) return
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file (PNG, JPG, or WebP).')
      ev.target.value = ''
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be 5MB or smaller.')
      ev.target.value = ''
      return
    }
    setError(null)
    if (logoCrop?.url) URL.revokeObjectURL(logoCrop.url)
    setLogoCrop({ url: URL.createObjectURL(file), mime: file.type })
  }

  function handleBannerFile(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0]
    if (!file || storeId == null) return
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file (PNG, JPG, or WebP).')
      ev.target.value = ''
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be 5MB or smaller.')
      ev.target.value = ''
      return
    }
    setError(null)
    if (bannerCrop?.url) URL.revokeObjectURL(bannerCrop.url)
    setBannerCrop({ url: URL.createObjectURL(file), mime: file.type })
  }

  async function uploadCroppedLogo(file: File) {
    if (storeId == null) return
    closeLogoCrop()
    setUploadingLogo(true)
    setUploadProgress(0)
    setError(null)
    try {
      const urls = await uploadProductImages([file], setUploadProgress, { purpose: 'logo' })
      const url = urls[0]
      if (!url) throw new Error('Upload did not return a URL')
      await storeApi.updateStore(storeId, { logo: url })
      setLogoUrl(url)
      await refreshStores()
      openSuccess({
        title: 'Logo saved',
        description: 'Your store header logo is updated on the storefront.',
      })
    } catch (e) {
      openError({
        title: 'Could not upload logo',
        description: getUserFacingErrorMessage(e, 'Failed to upload logo'),
      })
    } finally {
      setUploadingLogo(false)
      setUploadProgress(0)
    }
  }

  async function uploadCroppedBanner(file: File) {
    if (storeId == null) return
    closeBannerCrop()
    setUploadingBanner(true)
    setUploadProgress(0)
    setError(null)
    try {
      const urls = await uploadProductImages([file], setUploadProgress)
      const url = urls[0]
      if (!url) throw new Error('Upload did not return a URL')
      await storeApi.updateStore(storeId, { aboutBannerUrl: url })
      setAboutBannerUrl(url)
      await refreshStores()
      openSuccess({
        title: 'Banner saved',
        description: 'The wide image on your About page has been updated.',
      })
    } catch (e) {
      openError({
        title: 'Could not upload banner',
        description: getUserFacingErrorMessage(e, 'Failed to upload banner'),
      })
    } finally {
      setUploadingBanner(false)
      setUploadProgress(0)
    }
  }

  async function handleAiTagline() {
    if (storeId == null) return
    setAiTaglineLoading(true)
    setError(null)
    try {
      const { text } = await draftProfileText('tagline')
      setTagline(text.slice(0, storeApi.STORE_PROFILE_LIMITS.tagline))
      openInfo({
        title: 'Tagline generated',
        description: 'Review the text, then click Save store profile to publish it on your shop.',
      })
    } catch (e) {
      openError({
        title: 'Could not generate tagline',
        description: getUserFacingErrorMessage(e, 'Could not generate tagline'),
      })
    } finally {
      setAiTaglineLoading(false)
    }
  }

  async function handleAiAbout() {
    if (storeId == null) return
    setAiAboutLoading(true)
    setError(null)
    try {
      const { text } = await draftProfileText('about')
      setAboutText(text.slice(0, storeApi.STORE_PROFILE_LIMITS.aboutText))
      openInfo({
        title: 'About text generated',
        description: 'Review the text, then click Save store profile to publish it on your shop.',
      })
    } catch (e) {
      openError({
        title: 'Could not generate About text',
        description: getUserFacingErrorMessage(e, 'Could not generate About text'),
      })
    } finally {
      setAiAboutLoading(false)
    }
  }

  async function handleSaveProfile() {
    if (storeId == null) return
    setProfileSaving(true)
    setError(null)
    const lim = storeApi.STORE_PROFILE_LIMITS
    if (aboutText.length > lim.aboutText) {
      setError(`About text must be at most ${lim.aboutText} characters.`)
      setProfileSaving(false)
      return
    }
    if (tagline.length > lim.tagline) {
      setError(`Tagline must be at most ${lim.tagline} characters.`)
      setProfileSaving(false)
      return
    }
    if (sellingSummary.length > lim.sellingSummary) {
      setError(`What this shop sells must be at most ${lim.sellingSummary} characters.`)
      setProfileSaving(false)
      return
    }
    if (contactAddress.length > lim.contactAddress) {
      setError(`Address must be at most ${lim.contactAddress} characters.`)
      setProfileSaving(false)
      return
    }
    if (businessHours.length > lim.businessHours) {
      setError(`Business hours must be at most ${lim.businessHours} characters.`)
      setProfileSaving(false)
      return
    }
    try {
      const trimmedWhatsapp = whatsapp.trim()
      await storeApi.updateStore(storeId, {
        aboutText: aboutText.trim() || null,
        tagline: tagline.trim() || null,
        sellingSummary: sellingSummary.trim() || null,
        contactAddress: contactAddress.trim() || null,
        businessHours: businessHours.trim() || null,
        contactEmail: contactEmail.trim() || null,
        contactPhone: contactPhone.trim() || null,
        socialLinks: { whatsapp: trimmedWhatsapp || '' },
      })
      await refreshStores()
      openSuccess({
        title: 'Store profile saved',
        description: 'Niche, contact details, and About content are updated on your storefront.',
      })
    } catch (e) {
      openError({
        title: 'Could not save store profile',
        description: getUserFacingErrorMessage(e, 'Failed to save store profile'),
      })
    } finally {
      setProfileSaving(false)
    }
  }

  const lim = storeApi.STORE_PROFILE_LIMITS
  const activeStore = storeRecord
  const checklistDismissed = Boolean(activeStore?.onboardingChecklistDismissedAt)

  async function handleShowChecklistAgain() {
    if (storeId == null) return
    setChecklistResetting(true)
    setError(null)
    try {
      await storeApi.updateStore(storeId, { onboardingChecklistDismissedAt: null })
      await refreshStores()
      openSuccess({
        title: 'Setup checklist restored',
        description: 'The getting-started checklist will appear on your dashboard again.',
      })
    } catch (e) {
      openError({
        title: 'Could not restore checklist',
        description: getUserFacingErrorMessage(e, 'Failed to restore setup checklist'),
      })
    } finally {
      setChecklistResetting(false)
    }
  }

  return (
    <div className="w-full max-w-none space-y-6">
      <StatusModal {...statusModalProps} />
      <ImageCropDialog
        open={logoCrop != null}
        imageUrl={logoCrop?.url ?? null}
        sourceMimeType={logoCrop?.mime}
        aspect={1}
        title="Crop logo"
        description="Frame your logo — wide or square both work. Empty margins are removed automatically."
        preferPng
        trimMargins
        maxEdge={1024}
        fileName="store-logo"
        onCancel={closeLogoCrop}
        onConfirm={(file) => void uploadCroppedLogo(file)}
      />
      <ImageCropDialog
        open={bannerCrop != null}
        imageUrl={bannerCrop?.url ?? null}
        sourceMimeType={bannerCrop?.mime}
        aspect={21 / 9}
        title="Crop About banner"
        description="Frame a wide banner (21:9). Crop out unused space before uploading."
        maxEdge={1920}
        fileName="about-banner"
        onCancel={closeBannerCrop}
        onConfirm={(file) => void uploadCroppedBanner(file)}
      />
      <Modal
        open={previewImage != null}
        title={previewImage?.title ?? 'Image preview'}
        onClose={() => setPreviewImage(null)}
        size="xl"
      >
        {previewImage ? (
          <div className="flex max-h-[75vh] items-center justify-center overflow-auto rounded-lg bg-muted/30 p-2">
            <img
              src={previewImage.url}
              alt={previewImage.title}
              className="max-h-[70vh] w-auto max-w-full object-contain"
            />
          </div>
        ) : null}
      </Modal>
      {user?.role === 'org_admin' && !loading && storeRecord && checklistDismissed ? (
        <Card className="w-full max-w-none">
          <CardHeader
            title="Setup checklist"
            description="Bring back the getting-started checklist on your dashboard if you dismissed it."
          />
          <Button type="button" variant="secondary" disabled={checklistResetting} onClick={() => void handleShowChecklistAgain()}>
            {checklistResetting ? 'Saving…' : 'Show setup checklist again'}
          </Button>
        </Card>
      ) : null}
      <Card className="w-full max-w-none">
        <CardHeader
          title="Settings"
          description="Storefront currency, inventory alerts, sales tax, profile, and contact details shown to customers."
        />
        {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !storeRecord ? (
          <p className="text-sm text-muted-foreground">No store found for this organisation.</p>
        ) : (
          <div className="w-full max-w-none space-y-8">
            <div className="w-full space-y-6 rounded-xl border border-border bg-muted/15 p-5 md:p-8">
              <div>
                <h3 className="text-base font-semibold text-foreground">Store profile (customer storefront)</h3>
                <p className="mt-1 max-w-3xl text-xs text-muted-foreground md:text-sm">
                  This is what shoppers see on your About page — story, tagline, banner, and how to reach you.
                  Use blank lines in About to start a new paragraph. You can use “Generate with AI” to draft
                  text from what this shop sells.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10">
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Logo (header)</span>
                <p className="text-xs text-muted-foreground">
                  Crop to a tight square — remove empty margins so the mark fills the header.
                </p>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={handleLogoFile}
                />
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={uploadingLogo || uploadingBanner || storeId == null || logoCrop != null}
                    onClick={() => logoInputRef.current?.click()}
                  >
                    {uploadingLogo ? 'Uploading…' : 'Upload logo'}
                  </Button>
                  {logoUrl ? (
                    <button
                      type="button"
                      onClick={() => setPreviewImage({ url: logoUrl, title: 'Store logo' })}
                      className="rounded-lg border border-border bg-background/50 p-1 transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      title="View logo full size"
                    >
                      <img src={logoUrl} alt="Store logo preview" className="h-10 max-w-[160px] object-contain" />
                      <span className="sr-only">View logo full size</span>
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground">No logo yet</span>
                  )}
                </div>
                {uploadingLogo ? <UploadProgressBar active={uploadingLogo} label="Uploading…" progress={uploadProgress} /> : null}
              </div>

              <div className="flex min-h-0 flex-col space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">About page banner</span>
                <p className="text-xs text-muted-foreground">
                  Crop to a wide banner for the <strong className="text-foreground">About</strong> page only — not the
                  header logo.
                </p>
                <input
                  ref={bannerInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={handleBannerFile}
                />
                <Button
                  type="button"
                  variant="secondary"
                  className="w-fit"
                  disabled={uploadingBanner || uploadingLogo || storeId == null || bannerCrop != null}
                  onClick={() => bannerInputRef.current?.click()}
                >
                  {uploadingBanner ? 'Uploading…' : 'Upload banner'}
                </Button>
                <div className="w-full overflow-hidden rounded-xl border border-border bg-background/50">
                  {aboutBannerUrl ? (
                    <button
                      type="button"
                      onClick={() => setPreviewImage({ url: aboutBannerUrl, title: 'About page banner' })}
                      className="block w-full text-left transition-opacity hover:opacity-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                      title="View banner full size"
                    >
                      <img
                        src={aboutBannerUrl}
                        alt="About page banner preview"
                        className="aspect-[21/9] w-full max-h-56 object-cover sm:max-h-64"
                      />
                      <span className="sr-only">View banner full size</span>
                    </button>
                  ) : (
                    <div className="flex aspect-[21/9] min-h-[140px] w-full items-center justify-center bg-muted/40 text-xs text-muted-foreground">
                      No banner — upload a wide image (recommended 21:9)
                    </div>
                  )}
                </div>
                {uploadingBanner ? (
                  <UploadProgressBar active={uploadingBanner} label="Uploading…" progress={uploadProgress} />
                ) : null}
              </div>
              </div>

              <FaviconSettings
                storeId={storeId}
                value={faviconUrl}
                onSaved={async (url) => {
                  setFaviconUrl(url)
                  await refreshStores()
                  openSuccess({
                    title: url ? 'Tab icon saved' : 'Tab icon removed',
                    description: url
                      ? 'Your shop now shows its own icon in browser tabs. Browsers can take a while to refresh old icons.'
                      : 'Your shop is back to the Kartly icon in browser tabs.',
                  })
                }}
              />

              <label className="block w-full space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">What this shop sells</span>
                <textarea
                  className={textareaClass}
                  value={sellingSummary}
                  onChange={(e) => setSellingSummary(e.target.value)}
                  maxLength={lim.sellingSummary}
                  rows={3}
                  placeholder="e.g. Ready-to-wear dresses and custom sewing for women"
                />
                <p className="text-xs tabular-nums text-muted-foreground">
                  {sellingSummary.length}/{lim.sellingSummary}
                </p>
                <p className="text-xs text-muted-foreground">
                  A plain description of your products or services. Helps when drafting About text.
                </p>
              </label>

              <label className="block w-full space-y-1.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-medium text-muted-foreground">Tagline</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={aiTaglineLoading || storeId == null || uploadingLogo || uploadingBanner}
                    onClick={handleAiTagline}
                  >
                    {aiTaglineLoading ? 'Generating…' : 'Generate with AI'}
                  </Button>
                </div>
                <Input
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  maxLength={lim.tagline}
                  className="w-full"
                  placeholder="Short line under your store name"
                />
                <p className="text-xs tabular-nums text-muted-foreground">
                  {tagline.length}/{lim.tagline}
                </p>
              </label>

              <label className="block w-full space-y-1.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-medium text-muted-foreground">About</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={aiAboutLoading || storeId == null || uploadingLogo || uploadingBanner}
                    onClick={handleAiAbout}
                  >
                    {aiAboutLoading ? 'Generating…' : 'Generate with AI'}
                  </Button>
                </div>
                <textarea
                  className={textareaClass}
                  value={aboutText}
                  onChange={(e) => setAboutText(e.target.value)}
                  maxLength={lim.aboutText}
                  rows={8}
                  placeholder="Tell shoppers your story…"
                />
                <p className="text-xs tabular-nums text-muted-foreground">
                  {aboutText.length}/{lim.aboutText}
                </p>
              </label>

              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Contact email</span>
                <Input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  autoComplete="email"
                />
              </label>

              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Contact phone</span>
                <Input
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  autoComplete="tel"
                />
              </label>

              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">WhatsApp number</span>
                <Input
                  type="tel"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  autoComplete="tel"
                  placeholder="+234 801 234 5678"
                  maxLength={32}
                />
                <p className="text-xs text-muted-foreground">
                  Shown as a floating chat button across your shop. Include country code. Leave blank to hide it.
                </p>
              </label>

              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Address</span>
                <textarea
                  className={textareaClass}
                  value={contactAddress}
                  onChange={(e) => setContactAddress(e.target.value)}
                  maxLength={lim.contactAddress}
                  rows={3}
                  placeholder={STANDARD_ADDRESS_PLACEHOLDER}
                />
                <p className="text-xs tabular-nums text-muted-foreground">
                  {contactAddress.length}/{lim.contactAddress}
                </p>
              </label>

              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Business hours</span>
                <Input
                  value={businessHours}
                  onChange={(e) => setBusinessHours(e.target.value)}
                  maxLength={lim.businessHours}
                  placeholder="e.g. Mon–Fri 9:00–17:00"
                />
                <p className="text-xs tabular-nums text-muted-foreground">
                  {businessHours.length}/{lim.businessHours}
                </p>
              </label>

              <Button type="button" onClick={handleSaveProfile} disabled={profileSaving || storeId == null}>
                {profileSaving ? 'Saving…' : 'Save store profile'}
              </Button>
            </div>

            {user?.role === 'org_admin' ? (
              <div className="w-full max-w-none space-y-3 rounded-xl border border-border bg-muted/15 p-5 md:p-6">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Organisation country</h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Used for Kartly SaaS billing before your first payment. Nigeria is charged in NGN
                    (Paystack); other countries in EUR (Mollie).
                  </p>
                </div>
                <Select
                  id="organisation-country"
                  label="Country"
                  value={countryIso2}
                  onChange={(e) => setCountryIso2(e.target.value)}
                  disabled={countrySaving || countries.length === 0}
                >
                  <option value="">Select country…</option>
                  {countries.map((c) => (
                    <option key={c.iso2} value={c.iso2}>
                      {c.name}
                    </option>
                  ))}
                </Select>
                {billingPreview?.billingLocked ? (
                  <p className="text-xs text-muted-foreground">
                    Billing currency is locked to{' '}
                    <span className="font-medium text-foreground">
                      {billingPreview.billingCurrency ?? billingPreview.pendingBillingCurrency}
                    </span>{' '}
                    after first payment. Changing country does not switch gateway.
                  </p>
                ) : billingPreview?.pendingBillingCurrency ? (
                  <p className="text-xs text-muted-foreground">
                    You will be charged in{' '}
                    <span className="font-medium text-foreground">
                      {billingPreview.pendingBillingCurrency}
                    </span>{' '}
                    (
                    {billingPreview.pendingBillingProvider === 'paystack' ? 'Paystack' : 'Mollie'}
                    ).
                  </p>
                ) : null}
                <Button
                  type="button"
                  onClick={() => void handleSaveCountry()}
                  disabled={countrySaving || !countryIso2}
                >
                  {countrySaving ? 'Saving…' : 'Save organisation country'}
                </Button>
              </div>
            ) : null}

            <div className="w-full max-w-none space-y-3 rounded-xl border border-border bg-muted/15 p-5 md:p-6">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Storefront currency</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  What customers see on your online shop (prices, cart, checkout). This shop’s setting
                  wins over the company default.
                </p>
              </div>
              {(() => {
                const orgCur = orgDefaultCurrency(storeRecord)
                return (
                  <>
                    <Select
                      id="storefront-currency"
                      label="Currency for this shop"
                      value={currencyChoice}
                      onChange={(e) => setCurrencyChoice(e.target.value)}
                      disabled={currencySaving || storeId == null}
                    >
                      <option value="">
                        Use company default ({getCurrencyLabel(orgCur)}) — recommended
                      </option>
                      {CURRENCIES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.label}
                          {c.code === orgCur ? ' (same as company default)' : ''}
                        </option>
                      ))}
                    </Select>
                    {currencyChoice === '' || currencyChoice === orgCur ? (
                      <p className="text-xs text-muted-foreground">
                        Storefront will use{' '}
                        <span className="font-medium text-foreground">{getCurrencyLabel(orgCur)}</span>.
                      </p>
                    ) : (
                      <div
                        className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-950 dark:text-amber-100"
                        role="status"
                      >
                        <p className="font-semibold">Store override active</p>
                        <p className="mt-1">
                          Customers will see{' '}
                          <span className="font-medium">{getCurrencyLabel(currencyChoice)}</span>, not the
                          company default ({getCurrencyLabel(orgCur)}). Choose “Use company default” if
                          this shop should price in {getCurrencyLabel(orgCur)}.
                        </p>
                      </div>
                    )}
                    <Button
                      type="button"
                      onClick={() => void handleSaveCurrency()}
                      disabled={currencySaving || storeId == null}
                    >
                      {currencySaving ? 'Saving…' : 'Save storefront currency'}
                    </Button>
                  </>
                )
              })()}
            </div>

            <div className="w-full max-w-none space-y-3 rounded-xl border border-border bg-muted/15 p-5 md:p-6">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Storefront languages</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Choose the languages customers can switch between. The default language is used for new
                  visitors and for emails when a customer has not picked one.
                </p>
              </div>
              <Select
                id="default-locale"
                label="Default language"
                value={defaultLocaleChoice}
                onChange={(e) => handleDefaultLocaleChange(e.target.value as StoreLanguageCode)}
                disabled={languagesSaving || storeId == null}
              >
                {STORE_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.flag} {l.label}
                  </option>
                ))}
              </Select>
              {defaultLocaleIsChanging ? (
                <p className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-950 dark:text-amber-100" role="status">
                  <span className="font-semibold">Changing the default language.</span> Your product and store text will be
                  treated as written in {languageLabel(defaultLocaleChoice)}. Automatic translations will be redone and
                  translations you edited will be flagged for review. Only change it if your text really is in{' '}
                  {languageLabel(defaultLocaleChoice)}.
                </p>
              ) : null}
              <fieldset className="space-y-2" disabled={languagesSaving || storeId == null}>
                <legend className="text-xs font-medium text-muted-foreground">Languages offered to customers</legend>
                {STORE_LANGUAGES.map((l) => {
                  const isDefault = l.code === defaultLocaleChoice
                  return (
                    <label key={l.code} className="flex items-center gap-2.5 text-sm text-foreground">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-border accent-primary"
                        checked={supportedLocalesChoice.includes(l.code)}
                        disabled={isDefault}
                        onChange={(e) => toggleSupportedLocale(l.code, e.target.checked)}
                      />
                      <span>
                        {l.flag} {l.label}
                      </span>
                      {isDefault ? <span className="text-xs text-muted-foreground">(default)</span> : null}
                    </label>
                  )
                })}
              </fieldset>
              <p className="text-xs text-muted-foreground">
                Menus, buttons, checkout and customer emails are translated for you. Your products, categories, banners,
                offers and store text are translated automatically too: review them on each product and on the
                Translations page. Items that haven’t been translated yet show your original text.
              </p>
              <Button type="button" onClick={() => void handleSaveLanguages()} disabled={languagesSaving || storeId == null}>
                {languagesSaving ? 'Saving…' : 'Save languages'}
              </Button>
            </div>

            <div className="w-full max-w-none rounded-xl border border-border bg-muted/15 p-5 space-y-3 md:p-6">
              <h3 className="text-sm font-semibold text-foreground">Inventory alerts</h3>
              <p className="text-xs text-muted-foreground">
                Products at or below this quantity are flagged as low stock on the dashboard and product list.
                Out-of-stock items (0 units) are always highlighted.
              </p>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Low-stock threshold (units)</span>
                <Input
                  type="number"
                  min={0}
                  max={10000}
                  step={1}
                  value={lowStockThreshold}
                  onChange={(e) => setLowStockThreshold(e.target.value)}
                  className="max-w-[12rem]"
                />
              </label>
              <Button type="button" onClick={handleSaveLowStockThreshold} disabled={inventorySaving || storeId == null}>
                {inventorySaving ? 'Saving…' : 'Save inventory threshold'}
              </Button>
            </div>

            <div className="w-full max-w-none rounded-xl border border-border bg-muted/15 p-5 space-y-3 md:p-6">
              <h3 className="text-sm font-semibold text-foreground">Sales tax (checkout)</h3>
              <p className="text-xs text-muted-foreground">
                Percentage applied to the order subtotal (after line discounts) at checkout. Shoppers see tax as a
                separate line. Set to 0 if you do not collect tax.
              </p>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Tax rate (%)</span>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={taxRatePercent}
                  onChange={(e) => setTaxRatePercent(e.target.value)}
                  className="max-w-[12rem]"
                />
              </label>
              <Button type="button" onClick={handleSaveTax} disabled={saving || storeId == null}>
                {saving ? 'Saving…' : 'Save tax rate'}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
